import { gql, MutationFunctionOptions, useMutation } from '@apollo/client';
import {
  ComplianceMetaData,
  Question,
  QuestionPollingInterval,
  Query,
} from '@jupiterone/jupiter-types';
import { useSnackbarNotification } from '../components/notifications/SnackbarNotificationContext';
import stripTypeName from '../components/util/stripTypeName';
import removeDeep from '../util/removeDeep';

export const UpdateQuestionMutation = gql`
  mutation UpdateQuestion($id: ID!, $update: QuestionUpdate!) {
    updateQuestion(id: $id, update: $update) {
      id
      title
      name
      description
      queries {
        name
        query
        version
        resultsAre
      }
      compliance {
        standard
        requirements
        controls
      }
      variables {
        name
        required
        default
      }
      tags
      accountId
      integrationDefinitionId
      showTrend
      pollingInterval
    }
  }
`;

export type UpdateQuestion_QuestionUpdate = {
  title?: string;
  queries?: Query[];
  compliance?: ComplianceMetaData[];
  tags?: string[];
  description?: string;
  showTrend?: boolean;
  pollingInterval?: QuestionPollingInterval;
};

export type UpdateQuestionVariables = {
  id: string;
  update: UpdateQuestion_QuestionUpdate;
};

export type UpdateQuestionResponse = {
  updateQuestion: Question;
};

const sanitizeQuestionUpdate = (q: Question) => {
  const question = { ...stripTypeName(q) } as Question;
  const invalidFields: (keyof Question)[] = [
    'sourceId',
    'id',
    'variables',
    'accountId',
    'integrationDefinitionId',
    'name',
  ];
  invalidFields.forEach((field) => {
    delete question[field];
  });
  question.queries.forEach((_, i) => {
    delete question.queries[i].lastResult;
  });
  return removeDeep(
    question,
    (value: any) => value === null
  ) as UpdateQuestionVariables['update'];
};

type Options = Omit<
  MutationFunctionOptions<UpdateQuestionResponse, UpdateQuestionVariables>,
  'variables'
> & {
  variables: {
    question: Question;
  };
};

export const useUpdateQuestion = () => {
  const notification = useSnackbarNotification();
  const [updateQuestion] = useMutation<
    UpdateQuestionResponse,
    UpdateQuestionVariables
  >(UpdateQuestionMutation);

  return async ({ variables: { question }, ...options }: Options) => {
    try {
      const { data, ...response } = await updateQuestion({
        variables: {
          id: question.id,
          update: sanitizeQuestionUpdate(question),
        },
        ...options,
      });
      return { questionUpdate: data?.updateQuestion, ...response };
    } catch (e) {
      notification.error({
        message: 'Error saving question',
        error: e,
      });
    }
  };
};
